import axios from 'axios';
import routeData from '../../src/config/routes.json';
import { DateFormat } from './types';
import moment from 'moment';
import { REACT_APP_BASE_URL, REACT_APP_ENV } from '../config';
import { ROLES, TIME_RANGE, USER } from '../constants';

import 'moment/locale/de';
import 'moment/locale/en-gb';

// Helper function to format time to "hh:mm AM/PM"
const formatTime = (timestamp: number) => {
  const date = new Date(timestamp);
  const options = { hour: '2-digit', minute: '2-digit', hour12: true } as const;
  return date.toLocaleTimeString('en-US', options);
};

// Helper function to format date like WhatsApp (Today, Yesterday, or '5 Oct')
const formatDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set today's date to midnight
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1); // Set yesterday's date to midnight

  if (date >= today) {
    return 'Today';
  } else if (date >= yesterday) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }); // Format to '5 Oct'
  }
};

const convertToFormattedTime = (date: string | Date, locale: string = 'de'): string => {
  if (!date) return '';

  moment.locale(locale);
  const m = moment(date);

  // Always force month-first English-style order
  const datePart = `${m.format('MMM DD, YYYY')}`;
  const timePart = m.format('HH:mm');

  // Append "Uhr" only for German
  const formattedTime = locale === 'de' ? `${timePart} Uhr` : timePart;

  return `${datePart} | ${formattedTime}`;
};

const getPathByName = (name: string) => {
  const route = routeData?.find((r: { name: string }) => r.name === name);
  return route ? route.path : null;
};

const getDate = (date: string) => {
  return date
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short', // Abbreviated month (e.g., Apr)
        day: '2-digit', // Day with 2 digits (e.g., 23)
        year: 'numeric' // Full year (e.g., 2021)
      })
        .format(new Date(date))
        .replace('', '') + // Removes the default comma
        ' | ' +
        new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }).format(new Date(date))
    : 'N/A';
};

const addSpace = (data?: string) => {
  return data?.replace(/,/g, ', ') || 'N/A';
};

interface Email {
  assignedRole: string;
  practiseName: string;
  assignedBy: string;
  from: string;
  senderName: string;
  to: string;
  recipientName: string;
  practiseCode?: string;
}

const TEMPLATE_IDS = {
  test: {
    SUPER_ADMIN: 6706471,
    PRACTISE_ADMIN: 7253374
  },
  uat: {
    SUPER_ADMIN: 7503782,
    PRACTISE_ADMIN: 7503783
  },
  prod: {
    SUPER_ADMIN: 7616807,
    PRACTISE_ADMIN: 7616806
  }
} as const;

const getTemplateId = (env: string, role: string): number => {
  const group =
    env === 'uat' ? TEMPLATE_IDS.uat : env === 'prod' ? TEMPLATE_IDS.prod : TEMPLATE_IDS.test;

  if (role === ROLES.SUPER_ADMIN) return group.SUPER_ADMIN;
  if (role === ROLES.PRACTISE_ADMIN) return group.PRACTISE_ADMIN;

  throw new Error(`Unknown role: ${role}`);
};

const sendEmail = async (email: Email, userRole: string, t: (arg: string) => string) => {
  const userName = '7df5cbcb91c972735f86906376860186';
  const password = '39aac2211f272b2223c3981e4de08104';
  const credentials = btoa(`${userName}:${password}`);

  const envKey = REACT_APP_ENV === 'prod' ? 'prod' : 'test';
  const TemplateID = getTemplateId(envKey, userRole);

  const isSuperAdmin = userRole === ROLES.SUPER_ADMIN;
  const role = isSuperAdmin ? 'superadmin' : 'practiseadmin';

  const superAdminVariables = {
    greeting: t(`roleAssignment.${role}.greeting`),
    roleAssignmentText: t(`roleAssignment.${role}.subjectText`),
    feature1: t(`roleAssignment.${role}.features.manage`),
    feature2: t(`roleAssignment.${role}.features.assign`),
    feature3: t(`roleAssignment.${role}.features.verify`),

    practiceCodeLabel: t(`roleAssignment.${role}.practiceCodeLabel`),
    loginButtonText: t(`roleAssignment.${role}.loginButton`),

    errorText: t(`roleAssignment.${role}.errorText`),
    footerNote: t(`roleAssignment.${role}.footerNote`),

    appStoreSmallText: t(`roleAssignment.${role}.appStore.small`),
    appStoreBigText: t(`roleAssignment.${role}.appStore.big`),
    playStoreSmallText: t(`roleAssignment.${role}.playStore.small`),
    playStoreBigText: t(`roleAssignment.${role}.playStore.big`),

    unsubscribeText: t(`roleAssignment.${role}.unsubscribe`),
    supportEmail: t(`roleAssignment.${role}.supportEmail`),

    copyRightText: t(`roleAssignment.${role}.copyRight`),
    assignedRoleLabel: t(`roleAssignment.${role}.assignedRoleLabel`),

    practice: t(`roleAssignment.${role}.practice`),
    assignedByLabel: t(`roleAssignment.${role}.assignedByLabel`)
  };

  const practiseAdminVariables = {
    // Translations for text content
    greeting: t(`roleAssignment.${role}.greeting`),
    roleAssignmentText: t(`roleAssignment.${role}.roleAssignmentText`),
    roleInfoText: t(`roleAssignment.${role}.roleInfoText`),
    loginButtonText: t(`roleAssignment.${role}.loginButtonText`),
    errorText: t(`roleAssignment.${role}.errorText`),
    footerNote: t(`roleAssignment.${role}.footerNote`),

    // App store and Play store texts
    appStoreSmallText: t(`roleAssignment.${role}.appStore.small`),
    appStoreBigText: t(`roleAssignment.${role}.appStore.big`),
    playStoreSmallText: t(`roleAssignment.${role}.playStore.small`),
    playStoreBigText: t(`roleAssignment.${role}.playStore.big`),

    // Support and footer
    unsubscribeText: t(`roleAssignment.${role}.unsubscribe`),
    supportEmail: t(`roleAssignment.${role}.supportEmail`),
    copyRightText: t(`roleAssignment.${role}.copyRight`)
  };
  const data = isSuperAdmin ? superAdminVariables : practiseAdminVariables;
  const variables = {
    recipientName: email.recipientName,
    assignedRole: email.assignedRole,
    practiseName: email.practiseName,
    assignedBy: email.assignedBy,
    ...data,
    ...(email.practiseCode && { practiseCode: email.practiseCode })
  };

  const payload = {
    data: {
      Messages: [
        {
          From: { Email: 'support@spicter.com', Name: 'MedTech Support' },
          To: [{ Email: email.to, Name: email.recipientName }],
          Variables: variables,
          Subject: t(`roleAssignment.superadmin.label`),
          TemplateID,
          TemplateLanguage: true
        }
      ]
    }
  };

  const token = localStorage.getItem('user_access_token');
  if (!token) return;

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      MailjetAuthorization: `Basic ${credentials}`,
      'Content-Type': 'application/json'
    }
  };

  try {
    const url = `${REACT_APP_BASE_URL}/v1/send-email`;
    const response = await axios.post(url, payload, config);
    console.log('Email Response:', response.data);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Error sending email:', error.response?.data ?? error.message);
  }
};

const dateFormat = {
  DD_MM_YYYY: 'DD/MM/YYYY'
};
const getFormattedDate = (date?: Date, format: DateFormat = dateFormat.DD_MM_YYYY): string => {
  return moment(date).format(format);
};

const getPractiseType = (role: string) => {
  if (role === ROLES.SUPER_ADMIN) return USER.PRACTICE_ADMIN_OWNER;
  if (role === ROLES.PRACTISE_ADMIN) return USER.PRACTICE_ADMIN_ASSOCIATE;
};

const getTimeRangeValue = (value: string): string => {
  switch (value) {
    case TIME_RANGE.WEEKLY:
      return 'daywise';
    case TIME_RANGE.MONTHLY:
      return 'weekwise';
    case TIME_RANGE.YEARLY:
      return 'monthwise';
    default:
      return value;
  }
};

const getTranslatedDayLabel = (label: string, t: (key: string) => string): string => {
  if (!label) return '';

  const key = `days.${label.toLowerCase()}`;
  const translated = t(key);

  // If translation not found, t(key) returns the key itself (e.g. "days.mon")
  if (translated === key) {
    return label; // fallback to original label
  }

  return translated;
};
const getTranslatedWeekLabel = (label: string, t: (key: string) => string): string => {
  if (!label) return '';

  const translatedWeek = t('week');

  // If translation missing, t() often returns the key back
  const missingTranslation = translatedWeek === 'week' || translatedWeek.startsWith('week.');

  if (missingTranslation) {
    return label;
  }

  // Replace "Week" (case insensitive) with translated value
  return label.replace(/week/i, translatedWeek);
};

const getTranslatedMonthLabel = (label: string, t: (key: string) => string): string => {
  if (!label) return '';

  const parts = label.trim().split(' ');
  if (parts.length < 2) return label; // unexpected format

  const [month, year] = parts;
  if (!month || !year) return label;

  const key = month.toLowerCase(); // jan, january
  const translationKey = `months.${key}`;
  const translated = t(translationKey);

  // If translation not found → fallback to original month
  return `${translated === translationKey ? month : translated} ${year}`;
};

const translateLabelByTimeRange = (
  label: string,
  selectedValue: string,
  t: (key: string) => string
): string => {
  if (!label) return '';

  switch (selectedValue) {
    case TIME_RANGE.WEEKLY:
      return getTranslatedDayLabel ? getTranslatedDayLabel(label, t) : label;

    case TIME_RANGE.MONTHLY:
      return getTranslatedWeekLabel ? getTranslatedWeekLabel(label, t) : label;

    case TIME_RANGE.YEARLY:
      return getTranslatedMonthLabel(label, t) || label;

    default:
      return label;
  }
};

const translateWithFallback = (
  group: string,
  value: string,
  t: (key: string) => string
): string => {
  if (!value) return '';

  // normalize key: remove spaces + lowercase first letter
  const key = value
    .trim()
    .replace(/\s+/g, '')
    .replace(/^\w/, (c) => c.toLowerCase());

  const translated = t(`${group}.${key}`);

  // Fallback → return original if not found
  return translated === `${group}.${key}` ? value : translated;
};

const getTranslatedGender = (gender: string, t: (key: string) => string): string => {
  return gender ? t(gender.toLowerCase()) : '';
};

const getTranslatedRole = (role: string, t: (key: string) => string) =>
  translateWithFallback('roles', role, t);

const getTranslatedFolder = (folder: string, t: (key: string) => string) =>
  translateWithFallback('folders', folder, t);

const getTranslatedFeedback = (title: string, t: (key: string) => string) =>
  translateWithFallback('feedbacks', title, t);

const getTranslatedLanguages = (
  languages: string | undefined,
  t: (key: string) => string
): string => {
  if (!languages) return '';

  return languages
    .split(',')
    .map((lang) => translateWithFallback('languages', lang.trim(), t))
    .join(', ');
};

const getDoctorVerificationSelectedTab = (): number => {
  const value = localStorage.getItem('doctorVerificationSelectedTab');
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

export {
  formatDate,
  formatTime,
  getPathByName,
  getDate,
  addSpace,
  sendEmail,
  getFormattedDate,
  getPractiseType,
  getTimeRangeValue,
  convertToFormattedTime,
  getTranslatedDayLabel,
  getTranslatedWeekLabel,
  getTranslatedMonthLabel,
  translateLabelByTimeRange,
  getTranslatedRole,
  getTranslatedGender,
  getTranslatedFolder,
  getTranslatedLanguages,
  getTranslatedFeedback,
  getDoctorVerificationSelectedTab
};
